import React, { useMemo, useState } from "react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { IconButton, Box, Tooltip, useTheme, TextField, Button, InputAdornment } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import dayjs from "dayjs";

const EMSX_ROUTER_KEY = '7b16a3667dcc8252a857d4a322d4e143';

const BrokerTable = ({ brokers = [], onEdit, onDelete, onAdd }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(1000); // Set to show all items by default

  const filteredRows = useMemo(() => {
    if (!search) return brokers;
    const s = search.toLowerCase();
    return brokers.filter(row =>
      (row.name || "").toLowerCase().includes(s) ||
      (row.abbreviation || "").toLowerCase().includes(s) ||
      String(row.commission || 0).includes(s) ||
      (row.routers && row.routers[EMSX_ROUTER_KEY]?.name?.toLowerCase().includes(s))
    );
  }, [brokers, search]);

  const columns = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 120 },
    { field: "abbreviation", headerName: "Abbreviation", flex: 1, minWidth: 100 },
    {
      field: "commission",
      headerName: "Default Commission",
      flex: 1,
      minWidth: 130,
      valueGetter: (params) => params.row.commission || 0,
    },
    {
      field: "routerEMSX",
      headerName: "Router (EMSX)",
      flex: 1,
      minWidth: 140,
      valueGetter: (params) => params.row.routers?.[EMSX_ROUTER_KEY]?.name || "N/A",
    },
    {
      field: "date",
      headerName: "Created",
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => {
        if (!params.row.date) return "-";
        const d = dayjs(params.row.date);
        return d.isValid() ? d.format("YYYY-MM-DD") : params.row.date;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton color="primary" size="small" onClick={() => params.row.onEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton color="error" size="small" onClick={() => params.row.onDelete(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      width: 100,
      align: 'center',
      headerAlign: 'center',
    },
  ];
  // Attach onEdit/onDelete to each row for easy access in renderCell
  const rows = (filteredRows || []).map((row) => ({ ...row, onEdit, onDelete, id: row.brokerId }));
  return (
    <Box sx={{  width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search brokers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Add Broker
        </Button>
      </Box>
      <DataGridPro
        rows={rows}
        columns={columns}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[10, 25, 50, 100, 500, 1000, 2000]}
        pagination
        density="compact"
        disableSelectionOnClick
        sx={{
          border: "none",
          height: "calc(100vh - 20px)",
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            fontWeight: 500,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.grey[100],
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
          },
        }}
      />
    </Box>
  );
};

export default BrokerTable;